import React, { useEffect, useMemo, useRef, useState } from 'react'
import { faBooks } from '@fortawesome/pro-solid-svg-icons'
import { RankmiTable } from '@rankmi/rankmi-table'
import { useDispatch, useSelector } from 'react-redux'

import {
  generateTableLocale,
  useTranslationRankmi,
  joinKeys,
} from 'helpers/translations.helper'
import { IMainState } from 'store/states'
import { canSkipOnRowSelected } from 'helpers'
import { Course, CourseUser } from 'graphqlAPI'
import { SortValue } from 'models/rankmiTable.model'
import createApplication, {
  CreateApplicationAttributes,
} from 'api/administration/applications/Create'
import { EnrolState, MonitoringStatus } from 'enum'
import * as fromActions from 'store/actions/enrols.action'
import {
  ParticipantsForm,
  IEnrolData,
} from 'components/synchronousCourses/ParticipantsForm'
import { useColumns } from './hooks/useColumns'
import {
  CHECKBOX_COLUMN_WIDTH,
  ACTION_COLUMN_WIDTH,
} from './hooks/useColumns/types'
import { prefix } from './constants'
import styles from './index.module.scss'

type Id = CourseUser['userId']

type DataColumn =
  | CourseUser & {
      action: Id
      key: Id
    }

export type Data = CourseUser

interface ITable {
  setPage(page: number): void
  setSearch(text: string): void
  setSort: (value: SortValue) => void
  setPerPage(perPage: number): void
  setSelectedRows(ids: Id[]): void
  selectedRows: (CourseUser | Id)[]
  setAllSelected(state: boolean): void
  list: Data[]
  total?: number
  loading: boolean
  currentPage: number
  course?: Course
  refetch?: () => void
}

const Table: React.FC<ITable> = ({
  setPage,
  setSearch,
  setSort,
  setPerPage,
  setSelectedRows,
  selectedRows,
  setAllSelected,
  list,
  loading,
  total,
  currentPage,
  course,
  refetch,
}) => {
  const tRelative = useTranslationRankmi({
    prefix: joinKeys(prefix),
  })
  const [dataSource, setDataSource] = useState<DataColumn[]>([])
  const { evaluationsCount = 0 } = useSelector(
    ({ courses }: IMainState) => courses.course
  )

  const dispatch = useDispatch()

  const translations = useMemo(
    () =>
      generateTableLocale({
        header: {
          rowsCount: tRelative('rowsCount'),
          rowsCount_plural: tRelative('rowsCount_plural'),
        },
      }),
    [tRelative]
  )

  useEffect(() => {
    setDataSource(
      list.map(({ userId, ...rest }) => ({
        id: userId,
        ...rest,
        userId,
        key: userId,
        action: userId,
      }))
    )
  }, [list])

  const [selected, setSelected] = useState<CourseUser | null>(null)

  const columns = useColumns({
    evaluationsCount,
    course,
    setSelected,
  })

  const containerRef = useRef<HTMLDivElement>(null)

  const tableScrollX = useMemo(
    () =>
      columns.reduce((sum, column) => sum + (column.width ?? 0), 0) +
      CHECKBOX_COLUMN_WIDTH,
    [columns]
  )

  const [scrollX, setScrollX] = useState(tableScrollX)

  useEffect(() => {
    setScrollX(tableScrollX)
  }, [tableScrollX])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateScroll = () => {
      const availableWidth = container.clientWidth
      setScrollX(availableWidth >= tableScrollX ? availableWidth : tableScrollX)
    }

    updateScroll()

    const resizeObserver = new ResizeObserver(updateScroll)
    resizeObserver.observe(container)
    window.addEventListener('resize', updateScroll)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateScroll)
    }
  }, [tableScrollX])

  const participantData = useMemo((): IEnrolData | null => {
    if (!selected) return null
    const { user, synchronousCourseEnrol, synchronousCourseEvaluations } =
      selected
    return {
      advance: selected.advance as MonitoringStatus,
      user: {
        name: user.name,
        lastname: user.lastname,
        avatar: user.avatar,
        position: user.position,
        identifier: user.identifier,
      },
      synchronousCourseEnrol: synchronousCourseEnrol
        ? {
            attendance: synchronousCourseEnrol?.attendance,
            finalState: synchronousCourseEnrol?.finalState,
            assessments: synchronousCourseEvaluations?.map((evaluation) => ({
              position: evaluation.position,
              qualification: evaluation.qualification,
            })),
          }
        : null,
    }
  }, [selected])
  return (
    <div
      ref={containerRef}
      className={styles.container}
      style={
        {
          '--monitor-action-column-width': `${ACTION_COLUMN_WIDTH}px`,
        } as React.CSSProperties
      }
    >
      <RankmiTable
        columnHeaders={columns}
        data={dataSource}
        selectedRows={[
          ...selectedRows.map((selected) =>
            typeof selected === 'string' ? selected : selected.userId
          ),
        ]}
        total={total}
        currentPage={currentPage}
        changeColumnsVisibility
        scroll={{ x: scrollX }}
        onPaginationChange={(page: any) => setPage(page)}
        onSearch={(text: string) => {
          setSearch(text)
        }}
        onSortChange={(sortData: any) => setSort(sortData)}
        loading={loading}
        onRowsPerPageChange={setPerPage}
        onRowSelected={({
          allSelected,
          unselectedRowKeys,
          selectedRowKeys,
          dispatcher,
        }: {
          allSelected: boolean
          unselectedRowKeys: Id[]
          selectedRowKeys: Id[]
          dispatcher: string
        }) => {
          if (canSkipOnRowSelected(dispatcher)) return
          setSelectedRows(
            allSelected ? unselectedRowKeys || [] : selectedRowKeys
          )
          setAllSelected(allSelected)
        }}
        locale={translations}
        countIcon={faBooks}
        initialRowsPerPage={10}
        simpleSelectAll
        responsive
      />
      <ParticipantsForm
        show={!!selected}
        data={participantData}
        onHide={() => {
          setSelected(null)
        }}
        onSave={async (values) => {
          const { enrol } = { ...selected }
          let enrolId = enrol?.id
          if (!course) return
          const courseId = course.id
          const synchronousCourseId = course.synchronousCourse?.id
          if (!enrolId && selected) {
            const newEnrol: CreateApplicationAttributes = {
              courseId: +courseId,
              userId: +selected.userId,
              stateAdmin: EnrolState.Approved,
              note: '',
            }
            const res = await createApplication(newEnrol).then(
              (res) => res.data
            )
            enrolId = res.id
          }
          if (enrolId) {
            if (!synchronousCourseId) return
            dispatch(
              fromActions.Actions.editSynchronousCourseEnrol({
                data: {
                  id: enrolId,
                  synchronousCourseEnrol: {
                    attendance: values.attendance,
                    finalState: values.finalState,
                    state: values.state,
                    synchronousCourseId: +synchronousCourseId,
                    synchronousCourseEnrolAssessments: values.notes?.map(
                      (note, i) => ({
                        qualification: +note,
                        position: i + 1,
                      })
                    ),
                  },
                },
                onSuccess: () => {
                  setSelected(null)
                  refetch?.()
                },
              })
            )
          }
        }}
      />
    </div>
  )
}

export default Table
